import { IonButton, IonContent, IonFooter, IonInput, IonModal, IonSpinner, IonToolbar, useIonToast } from '@ionic/react'
import { PatientStatus } from 'domain/usecases/list-patients'
import { PatientsService } from 'infra/services/patients-service'
import { pencil } from 'ionicons/icons'
import { ModalTitle, Typeahead } from 'presentation/components'
import { GroupedOption, TypeaheadItem } from 'presentation/components/typeahead/typeahead'
import React, { useCallback, useEffect, useState } from 'react'
import { useRecoilState } from 'recoil'
import { modalEditPatientAtom, ModalEditPatientState, reportToTypeaheadItem } from './modal-edit-patient-atom'
import './modal-edit-patient.scss'


type ModalEditPatientProps = React.ComponentPropsWithoutRef<typeof IonModal> & {
  modal: React.RefObject<HTMLIonModalElement>
  patientsService: PatientsService
  patientEdition: {
    mrn: string
    cancerType: string
    cancerStage: string
    status: string
    priorTreatments: string
    payer: string
    comorbidities: string
    address: string
    stages: string
    insurance_id_number: string
    group_id_number: string
  }
  fields: {
    cancerType: boolean
    stage: boolean
    status: boolean
    priorTreatments: boolean
    payer: boolean
    comorbidities: boolean
    insuranceId: boolean
    groupId: boolean
    address: boolean
  }
  headerTitle: string
}

const ModalEditPatient: React.FC<ModalEditPatientProps> = (props) => {
  const [editState, setEditState] = useRecoilState(modalEditPatientAtom)
  const [isSaving, setSaving] = useState(false)

  const [cancerTypes, setCancerTypes] = useState<GroupedOption[]>([])
  const [isLoadingCancerTypes, setLoadingCancerTypes] = useState(false)

  const [payers, setPayers] = useState<TypeaheadItem[]>([])
  const [isLoadingPayers, setLoadingPayers] = useState(false)

  const [stages, setStages] = useState<TypeaheadItem[]>([])

  const makeTypeahead = (label: string, value: number | string) => ({ value, label })
  const status = [makeTypeahead('Responding', 'responding'), makeTypeahead('Progressing', 'progressing')]

  const [presentToast] = useIonToast()

  const getCancerTypes = () => {
    setLoadingCancerTypes(true)
    props.patientsService
      .getCancerTypes({ from_reports: false })
      .then((result) => {
        if (result) {
          const newItens: GroupedOption[] = result.map((item) => ({ label: item.category, options: item.options }))
          setCancerTypes(newItens)
        } else setCancerTypes([])
      })
      .catch(() => setCancerTypes([]))
      .finally(() => setLoadingCancerTypes(false))
  }

  const getPayers = (adapter: string) => {
    setLoadingPayers(true)

    props.patientsService
      .getPayers({
        page: 1,
        search: adapter || '',
        size: 10,
      })
      .then((result) => {
        if (result) {
          const newItens: TypeaheadItem[] = result.items.map((item) => ({
            label: item,
            value: item,
          }))
          setPayers(newItens)
        } else setCancerTypes([])
      })
      .catch(() => setCancerTypes([]))
      .finally(() => setLoadingPayers(false))
  }

  const handleDebounceFn = useCallback((value: { [key: string]: string }) => {
    const adapter: { key: keyof ModalEditPatientState; value: string } = {
      key: Object.keys(value)[0] as keyof ModalEditPatientState,
      value: Object.values(value)[0],
    }

    switch (adapter.key) {
      case 'cancerType':
        getCancerTypes()
        break
      case 'payer':
        getPayers(adapter.value)
        break
      default:
        break
    }
  }, [])

  const getStages = () => {
    props.patientsService
      .getStages()
      .then((stages) => setStages(stages))
      .catch(() => setStages([]))
  }

  const save = () => {
    setSaving(true)
    props.patientsService
      .editPatient({
        mrn: props.patientEdition.mrn as string,
        cancer_stage: props.fields.stage ? editState.stages?.value as string : props.patientEdition.cancerStage,
        cancer_type: props.fields.cancerType ? editState.cancerType?.value as string : props.patientEdition.cancerType,
        status: props.fields.status ? editState.status?.value as PatientStatus : props.patientEdition.status as PatientStatus,
        prior_treatments: props.fields.priorTreatments ? editState.priorTreatments as string : props.patientEdition.priorTreatments,
        payer: props.fields.payer ? editState.payer?.value as string : props.patientEdition.payer,
        co_morbidities: props.fields.comorbidities ? editState.comorbidities as string : props.patientEdition.comorbidities,
        address: props.fields.address ? editState.address as string : props.patientEdition.address,
        insurance_id_number: props.fields.insuranceId ? editState.insurance_id_number as string : props.patientEdition.insurance_id_number,
        group_id_number: props.fields.groupId ? editState.group_id_number as string : props.patientEdition.group_id_number

      })
      .then(() => props.modal.current?.dismiss({ wasEdited: true }))
      .catch((message) => {
        presentToast({
          message,
          duration: 5000,
          color: 'danger',
        })
      })
      .finally(() => setSaving(false))
  }

  useEffect(() => {
    const values = {
      mrn: props.patientEdition.mrn,
      cancerType: { label: props.patientEdition.cancerType, value: props.patientEdition.cancerType },
      stages: { label: props.patientEdition.cancerStage, value: props.patientEdition.cancerStage },
      status: { label: props.patientEdition.status, value: props.patientEdition.status },
      priorTreatments: props.patientEdition.priorTreatments,
      payer: { label: props.patientEdition.payer, value: props.patientEdition.payer },
      comorbidities: props.patientEdition.comorbidities,
      address: props.patientEdition.address,
      insurance_id_number: props.patientEdition.insurance_id_number,
      group_id_number: props.patientEdition.group_id_number
    }
    setEditState(() => reportToTypeaheadItem(values) as ModalEditPatientState)
  }, [props.patientEdition])

  useEffect(() => {
    getStages()
  }, [])

  const [isInsuranceIdNumberValid, setIsInsuranceIdNumberValid] = useState(true)
  const [isGroupIdNumberValid, setIsGroupIdNumberValid] = useState(true)

  const handleInsuranceIdNumberInput = (e: any) => {
    const value = e.target.value as string
    const isValid = /^[a-zA-Z0-9-]*$/.test(value)
    setIsInsuranceIdNumberValid(isValid)
    setEditState((s) => ({ ...s, insurance_id_number: value } as ModalEditPatientState))
  }

  const handleGroupIdNumberInput = (e: any) => {
    const value = e.target.value as string
    const isValid = /^[a-zA-Z0-9-]*$/.test(value)
    setIsGroupIdNumberValid(isValid)
    setEditState((s) => ({ ...s, group_id_number: value } as ModalEditPatientState))
  }

  return (
    <>
      <IonContent className="ion-padding modal-edit-report-header">
        <ModalTitle icon={pencil} label={props.headerTitle} />
        {props.fields.cancerType ? (
          <Typeahead
            label="Cancer Type"
            placeholder="Select..."
            options={cancerTypes}
            isLoading={isLoadingCancerTypes}
            onMenuOpen={() => handleDebounceFn({ cancerType: '' })}
            onTypeaheadDebounce={(cancerType) => handleDebounceFn({ cancerType })}
            onChange={(cancerType) => setEditState((s) => ({ ...s, cancerType } as ModalEditPatientState))}
            value={editState.cancerType}
          />
        ) : undefined}
        {props.fields.stage ? (
          <Typeahead
            label="Stage"
            placeholder="Select..."
            options={stages}
            onTypeaheadDebounce={() => null}
            onChange={(stages) => setEditState((s) => ({ ...s, stages } as ModalEditPatientState))}
            value={editState.stages}
          />
        ) : undefined}
        {props.fields.status ? (
          <Typeahead
            label="Status"
            placeholder="Select..."
            options={status}
            onTypeaheadDebounce={(status) => handleDebounceFn({ status })}
            onChange={(status) => setEditState((s) => ({ ...s, status } as ModalEditPatientState))}
            value={editState.status}
          />
        ) : undefined}
        {props.fields.priorTreatments ? (
          <div className="free-text-input">
            <span>Prior Treatments</span>
            <IonInput
              value={editState.priorTreatments}
              onIonInput={(e) =>
                setEditState((s) => ({ ...s, priorTreatments: e.target.value } as ModalEditPatientState))
              }
              placeholder="-"
            />
          </div>
        ) : undefined}
        {props.fields.payer ? (
          <Typeahead
            label="Payer"
            placeholder="Unknown"
            options={payers}
            isLoading={isLoadingPayers}
            onMenuOpen={() => handleDebounceFn({ payer: '' })}
            onTypeaheadDebounce={(payer) => {
              handleDebounceFn({ payer })
            }}
            onChange={(payer) => setEditState((s) => ({ ...s, payer } as ModalEditPatientState))}
            value={editState.payer}
          />
        ) : undefined}
        {props.fields.comorbidities ? (
          <div className="free-text-input">
            <span>Co-morbidities</span>
            <IonInput
              value={editState.comorbidities}
              onIonInput={(e) =>
                setEditState((s) => ({ ...s, comorbidities: e.target.value } as ModalEditPatientState))
              }
              placeholder="-"
            />
          </div>
        ) : undefined}

        {props.fields.insuranceId ? (
          <div className="free-text-input">
            <span>Insurance ID Number</span>
            <IonInput value={editState.insurance_id_number} onIonInput={handleInsuranceIdNumberInput} placeholder="-" />
            {!isInsuranceIdNumberValid && (
              <span className="error-editing">A valid Insurance ID Number is required</span>
            )}
          </div>
        ) : undefined}

        {props.fields.groupId ? (
          <div className="free-text-input">
            <span>Group ID Number</span>
            <IonInput value={editState.group_id_number} onIonInput={handleGroupIdNumberInput} placeholder="-" />
            {!isGroupIdNumberValid && <span className="error-editing">A valid Group ID Number is required</span>}
          </div>
        ) : undefined}
        {props.fields.address ? (
          <div className="free-text-input">
            <span>Address</span>
            <IonInput
              value={editState.address}
              onIonInput={(e) => setEditState((s) => ({ ...s, address: e.target.value } as ModalEditPatientState))}
              placeholder="-"
            />
          </div>
        ) : undefined}
      </IonContent>

      <IonFooter>
        <IonToolbar>
          <IonButton slot="end" fill="outline" onClick={() => props.modal.current?.dismiss({ wasEdited: false })}>
            Cancel
          </IonButton>

          <IonButton
            slot="end"
            fill="solid"
            onClick={() => save()}
            disabled={isSaving || !isInsuranceIdNumberValid || !isGroupIdNumberValid}
          >
            {isSaving ? <IonSpinner /> : 'Save'}
          </IonButton>
        </IonToolbar>
      </IonFooter>
    </>
  )
}

export default ModalEditPatient
